/**
 * scheduleCapacity.ts
 * Per-machine utilization for the Machine Schedule capacity heatmap (UX — M6).
 *
 * Utilization = booked working-days ÷ working-days in the visible window.
 * Overlapping bookings count cumulatively, so a double-booked machine exceeds
 * 1.0 and surfaces as red (matching the ACTION-PLAN capacity guide:
 * 0–70% green / 71–100% yellow / >100% red).
 *
 * Weekends are excluded; stat-holiday awareness is a backend concern, so this
 * client-side indicator approximates working days as Mon–Fri.
 */

import type { MachineBookingCalendar } from '../types/schedule';

export type CapacityBand = 'low' | 'mid' | 'high';

function isWorkingDay(iso: string): boolean {
  const d = new Date(`${iso}T00:00:00`).getDay();
  return d !== 0 && d !== 6;
}

/** Number of working days within the visible day list. */
export function workingDaysInWindow(days: string[]): number {
  return days.reduce((n, d) => (isWorkingDay(d) ? n + 1 : n), 0);
}

/** Fractional utilization (0..n) for a machine's bookings over the window. */
export function machineUtilization(
  machineBookings: MachineBookingCalendar[],
  days: string[],
): number {
  const windowWorking = workingDaysInWindow(days);
  if (windowWorking === 0) return 0;
  let booked = 0;
  for (const day of days) {
    if (!isWorkingDay(day)) continue;
    for (const b of machineBookings) {
      if (day >= b.StartDate && day <= b.EndDate) booked += 1;
    }
  }
  return booked / windowWorking;
}

/** Map a utilization fraction to a heatmap band. */
export function capacityBand(util: number): CapacityBand {
  if (util > 1.0) return 'high';
  if (util > 0.7) return 'mid';
  return 'low';
}

// ---------------------------------------------------------------------------
// Heatmap theme token bag (shared by the band helpers)
// Colors are pulled from theme.schedule.* — no hex literals here.
// ---------------------------------------------------------------------------

/** Subset of theme.schedule tokens required by capacityBandBoard. */
export interface HeatmapTheme4 {
  heatmapLow: string;
  heatmapMid: string;
  heatmapElevated: string;
  heatmapHigh: string;
}

/**
 * Continuous capacity heatmap (replaces the 4 discrete bands): 0→100% darkens a
 * single sage hue (light = idle, dark = nearly full), and OVER 100% turns red,
 * deepening with severity. Returns CSS color strings (computed HSL) so the load
 * reads as a smooth ramp rather than four steps.
 *
 *   0%        → transparent (free)
 *   1–100%    → sage, lightness 90% (light) → 60% (filled) as it fills
 *   >100%     → red, lightness 46% → 30% as the overbook grows (≥200%)
 *
 * IMPORTANT — these cells carry a NUMBER, so the text must stay legible at every
 * point on the ramp. The sage lightness floor is held at ~60% (never entering the
 * mid-lightness "no-win" band where neither black nor white text passes WCAG) and
 * a single near-black sage text colour is used throughout — verified ≥4.5:1 at
 * every integer load by the contrast sweep in capacityHeatColor.test.ts. (The red
 * over-capacity branch is dark enough that white text clears 4.5:1.)
 */
export function capacityHeatColor(percent: number): { bg: string; fg: string } {
  const p = Number.isFinite(percent) ? Math.max(0, percent) : 0;
  if (p === 0) return { bg: 'transparent', fg: 'hsl(104, 10%, 42%)' };
  if (p > 100) {
    const over = Math.min(1, (p - 100) / 100); // 100→0 … 200%+→1
    const L = 46 - over * 16;                   // 46% → 30%
    return { bg: `hsl(2, 68%, ${L}%)`, fg: '#ffffff' };
  }
  const t = p / 100;
  const L = 90 - t * 30;                        // 90% (light) → 60% (filled, still legible)
  const S = 26 + t * 24;                        // 26% → 50%
  return { bg: `hsl(104, ${S}%, ${L}%)`, fg: 'hsl(104, 45%, 14%)' };
}

/**
 * CSS `linear-gradient(...)` that mirrors capacityHeatColor's 0→100% sage ramp,
 * for the capacity-table legend swatch. Sampling the same function keeps the
 * legend honest — if the ramp changes, the legend follows automatically.
 */
export function capacityHeatGradientCss(): string {
  const stops = [8, 30, 55, 80, 100].map((p) => capacityHeatColor(p).bg);
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/**
 * DISCRETE-band capacity color for the Board view — RE-KEYED on the Wave-3
 * BUSY FRACTION (busy days ÷ window days, 0–1) instead of a percent sum:
 *   0          → free (transparent)
 *   (0, 0.75)  → amber  (heatmapMid)  — partly busy, has slack
 *   [0.75, 1]  → green  (heatmapLow)  — well booked
 *   > 1        → red    (heatmapHigh) — should not occur under the binary model
 *                                       (kept for degenerate/legacy inputs)
 * For a single DAY the fraction is 0 or 1, so a busy day reads green.
 * Returns { bg, fg } like capacityHeatColor so the cell renders identically; the
 * Classic view keeps the sage fill. fg values are dark/white literals chosen
 * for ≥4.5:1 contrast against each solid band (utils may use literals; components
 * must not).
 */
export function capacityBandBoard(
  busyFraction: number,
  themeSchedule: HeatmapTheme4,
): { bg: string; fg: string } {
  const safe = Number.isFinite(busyFraction) ? Math.max(0, busyFraction) : 0;
  if (safe <= 0)   return { bg: 'transparent', fg: 'hsl(104, 10%, 42%)' };
  if (safe > 1)    return { bg: themeSchedule.heatmapHigh, fg: '#ffffff' };
  if (safe >= 0.75) return { bg: themeSchedule.heatmapLow,  fg: 'hsl(125, 55%, 15%)' };
  return               { bg: themeSchedule.heatmapMid,  fg: 'hsl(40, 90%, 16%)' };
}
